import { useCallback, useEffect, useRef } from 'react';
import { MIN_THUMB_SIZE } from '../constant/constants';

interface UseThumbsProps {
  scrollRef: React.RefObject<HTMLDivElement>;
  totalSize?: number;
  ios: boolean;
}

/**
 * Calculates scrollbar thumb size and position based on container and content size.
 *
 * @param scroll - Current scroll offset (scrollTop or scrollLeft).
 * @param containerSize - Visible size of the container (clientHeight or clientWidth).
 * @param totalSize - Total scrollable size (scrollHeight or scrollWidth or virtualized total size).
 * @param minSize - Minimum allowed size of the thumb.
 *
 * @returns An object containing:
 * - thumbSize: Calculated size of the scrollbar thumb.
 * - thumbPos: Calculated position (top/left) of the thumb.
 * - visible: Whether the scrollbar should be visible.
 */
const calculateThumb = (scroll: number, containerSize: number, totalSize: number, minSize: number) => {
  const maxScroll = Math.max(totalSize - containerSize, 0);
  const thumbSize = maxScroll > 0 ? Math.max((containerSize / totalSize) * containerSize, minSize) : minSize;
  const maxThumbPos = containerSize - thumbSize;
  const thumbPos = maxScroll > 0 ? Math.min((scroll / maxScroll) * maxThumbPos, maxThumbPos) : 0;

  return { thumbSize, thumbPos, visible: maxScroll > 0 };
};

/**
 * Custom React hook for managing iOS-style scroll thumbs (vertical & horizontal).
 *
 * This hook:
 * - Calculates and updates scrollbar thumb size and position
 * - Syncs thumb movement with scroll position
 * - Uses requestAnimationFrame for smooth updates
 * - Handles visibility of scrollbars based on content overflow
 *
 * @param scrollRef - Ref to the scrollable container element
 * @param totalSize - Optional total scrollable height (e.g. from virtualizer)
 * @param ios - Flag indicating if the environment is iOS (enables custom scroll behavior)
 *
 * @returns Object containing refs for vertical and horizontal scrollbar thumbs:
 * - vThumbRef: Ref for vertical scrollbar thumb
 * - hThumbRef: Ref for horizontal scrollbar thumb
 */
export const useThumbs = ({ scrollRef, totalSize, ios }: UseThumbsProps) => {
  const vThumbRef = useRef<HTMLDivElement>(null);
  const hThumbRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  /**
   * Updates scrollbar thumbs (size, position, visibility)
   * based on current scroll state and container dimensions.
   */
  const updateThumbs = useCallback(() => {
    const container = scrollRef.current;
    const vThumb = vThumbRef.current;
    const hThumb = hThumbRef.current;

    if (!ios || !container || !vThumb || !hThumb) return;

    const totalHeight = totalSize ?? container.scrollHeight;
    const totalWidth = container.scrollWidth;

    // Vertical
    const {
      thumbSize: vThumbHeight,
      thumbPos: vThumbTop,
      visible: vVisible,
    } = calculateThumb(container.scrollTop, container.clientHeight, totalHeight, MIN_THUMB_SIZE);

    vThumb.style.height = `${vThumbHeight}px`;
    vThumb.style.transform = `translateY(${vThumbTop}px)`;
    vThumb.style.opacity = vVisible ? '1' : '0';

    // Horizontal
    const {
      thumbSize: hThumbWidth,
      thumbPos: hThumbLeft,
      visible: hVisible,
    } = calculateThumb(container.scrollLeft, container.clientWidth, totalWidth, MIN_THUMB_SIZE);

    hThumb.style.width = `${hThumbWidth}px`;
    hThumb.style.transform = `translateX(${hThumbLeft}px)`;
    hThumb.style.opacity = hVisible ? '1' : '0';
  }, [ios, scrollRef, totalSize]);

  /**
   * Scroll event handler optimized with requestAnimationFrame
   * to prevent excessive reflows and improve performance.
   */
  const handleScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      updateThumbs();
      rafRef.current = null;
    });
  }, [updateThumbs]);

  /**
   * Sets up scroll and resize listeners.
   * Also triggers initial thumb calculation on mount and when dependencies change.
   */
  useEffect(() => {
    if (!ios) return;
    const container = scrollRef.current;
    if (!container) return;

    updateThumbs();

    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateThumbs);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateThumbs);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [ios, handleScroll, updateThumbs, scrollRef, totalSize]);

  return { vThumbRef, hThumbRef };
};
